// ============================================
// Định nghĩa DÙNG CHUNG cho việc đếm đơn theo trạng thái.
// Cả DashboardPage (Total Orders, Operations Overview) và OrdersPage (stats
// cards) đều phải đi qua file này — sửa một chỗ, khớp mọi trang.
//
// Nguồn dữ liệu: /reports/order-status-trend/ (đếm phía BE, không phụ thuộc
// page_size khi fetch danh sách đơn).
// ============================================

// Mốc "từ đầu" khi cần đếm all-time. LUÔN truyền ngày tường minh cho
// /orders/stats/ — default của BE khi bỏ trống ngày không phải all-time.
export const ALL_TIME_START = '2020-01-01';

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// Khớp OrderStatus 11 giá trị của BE (common/enums.py).
export const ORDER_STATUS_GROUPS = {
  pending: ['DRAFT', 'PENDING_PAYMENT'],
  processing: ['CONFIRMED', 'PREPARING'],
  ready: ['READY'],
  completed: ['COMPLETED', 'PAID'],
} as const;

// Nhóm huỷ/hoàn: hiển thị riêng, không cộng vào tổng "đơn hợp lệ".
export const ORDER_STATUSES_EXCLUDED_FROM_TOTAL = new Set([
  'CANCELLED',
  'VOIDED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
]);

export type OrderStatusTrendLike = {
  data?: Array<{ status_counts?: Record<string, unknown> }>;
};

export interface AggregatedOrderStatus {
  pending: number;
  processing: number;
  ready: number;
  completed: number;
  /** Đơn thuộc nhóm huỷ/hoàn — không nằm trong total. */
  cancelled: number;
  /** Status lạ chưa phân nhóm — vẫn cộng vào total để không mất đơn. */
  other: number;
  total: number;
  /** Đếm thô theo từng status, cho trang cần tách nhỏ hơn 4 nhóm. */
  counts: Record<string, number>;
}

function toCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

/** Gom một map status -> count theo nhóm dùng chung. */
export function aggregateStatusCounts(
  rawCounts: Record<string, unknown> | undefined
): AggregatedOrderStatus {
  const counts: Record<string, number> = {};

  for (const [rawStatus, rawCount] of Object.entries(rawCounts ?? {})) {
    const status = rawStatus.trim().toUpperCase();
    counts[status] = (counts[status] ?? 0) + toCount(rawCount);
  }

  return aggregateFromCounts(counts);
}

/** Gom status_counts của /reports/order-status-trend/ theo nhóm dùng chung. */
export function aggregateOrderStatusTrend(
  trendData: OrderStatusTrendLike | undefined
): AggregatedOrderStatus {
  const counts: Record<string, number> = {};

  for (const point of trendData?.data ?? []) {
    for (const [rawStatus, rawCount] of Object.entries(point.status_counts ?? {})) {
      const status = rawStatus.trim().toUpperCase();
      counts[status] = (counts[status] ?? 0) + toCount(rawCount);
    }
  }

  return aggregateFromCounts(counts);
}

function aggregateFromCounts(counts: Record<string, number>): AggregatedOrderStatus {

  const sumOf = (statuses: readonly string[]) =>
    statuses.reduce((acc, status) => acc + (counts[status] ?? 0), 0);

  const grouped = {
    pending: sumOf(ORDER_STATUS_GROUPS.pending),
    processing: sumOf(ORDER_STATUS_GROUPS.processing),
    ready: sumOf(ORDER_STATUS_GROUPS.ready),
    completed: sumOf(ORDER_STATUS_GROUPS.completed),
  };

  const cancelled = sumOf([...ORDER_STATUSES_EXCLUDED_FROM_TOTAL]);

  const groupedStatuses = new Set<string>(Object.values(ORDER_STATUS_GROUPS).flat());

  let other = 0;
  for (const [status, count] of Object.entries(counts)) {
    if (groupedStatuses.has(status) || ORDER_STATUSES_EXCLUDED_FROM_TOTAL.has(status)) continue;
    other += count;
    if (import.meta.env.DEV) {
      console.warn(`[orderStatus] Order status chưa được phân nhóm: "${status}"`);
    }
  }

  const total = grouped.pending + grouped.processing + grouped.ready + grouped.completed + other;

  return { ...grouped, cancelled, other, total, counts };
}
